import type { Action, ActionCreator } from 'redux';
import type { ThunkAction } from 'redux-thunk';
import { v4 as uuidv4 } from 'uuid';
import {
    trackGA4PreNav,
    trackGA4Event,
    trackGA4EventAfterPageView,
} from '@gumtree/shared/src/util/ga4-shared';
import deepMerge from 'lodash/merge';

import type { SyiState } from './index';
import {
    computeListingDetails,
    postAdPageTypeToAdEventName,
    type LocationCrumb,
    type PostAdImageDef,
    type SyiModelAttrs,
} from '../services/syi-model';
import type { SyiPageData } from '../get-data/transform';
import type { SyiContextExtra } from '../get-data';
import { dtlSyi, PricingMetadata } from '../services/dtl-syi';
import type { VrnResponse } from '../components/panels/vehicle/vrn-lookup';
import { TriggerPoint } from '@gumtree/ui-library/src/phone-verification/constants';
import { PanelNameMapping } from '../components/all-components-definitions';
import {
    getCookieValue,
    setCookieValue,
    tracklogKeyOnSyi,
} from '@gumtree/shared/src/util/ga4-postponer';

export const UPDATE_SYI = 'UPDATE_SYI';
export const UPDATE_SYI_FORM_ERROR = 'UPDATE_SYI_FORM_ERROR';
export const UPDATE_SYI_PHONE_VERIFY = 'UPDATE_SYI_PHONE_VERIFY';
export const GROWTHBOOK_VALUE_SYI_TITLE_FIRST = 'B';
export const GROWTHBOOK_VALUE_SYI_PHOTO_FIRST = 'C';
export const CATEGORY_ID_FOR_SALE = 2549;

export interface SyiReducerState {
    /** Constant over page lifetime */
    context: SyiContextExtra & SyiPageData;
    /** Determined by API and determines panels */
    mappedPanels: MappedPanel[];
    remountedAt: number;

    l1Category: string;
    categorySeoNames: string[];
    catList: CatSuggestionResponse | null;
    expandCategory: boolean;
    expandCategoryBrowser: boolean;
    expandLocationBrowser: boolean;
    expandEditLocation: boolean;
    enableEditContactDetails: boolean;
    radioCategoryType: SyiRadioCategoryType | null;
    radioCategoryId: number | null;
    radioShowAlert: boolean;

    /** SellerSkills */
    categorySkills: Skill[];
    isEditedSkills: boolean;
    accountCategorySkills: number[];
    showSellerSkillsError: boolean;

    /** Used to trigger component updates */
    updateCatsAt: number;
    openUrgentLightbox: boolean;
    openFeaturedLightbox: boolean;
    openSpotlightLightbox: boolean;
    openWebsiteLinkLightbox: boolean;
    openSellerTypeLightbox: boolean;
    openListingCapModal: boolean;
    hasSavedLocation: boolean;
    isPostingAd: boolean;

    formError: {
        [elName: string]: {
            elName: string;
            errors: string[];
            shouldShow: boolean;
        };
    };
    imageDefs: SyiImageDef[];
    vrnResponse: null | VrnResponse;
    /** vrnResponse can be null when initialisation AND API fetch error occurs, this is for differentiating if a fetch has performed */
    hasFetchVrn: boolean;
    /** GBP */
    totalPrice?: number;
    selectedListingType: string | null;
    selectedPetsBreed: string | null;
    selectedPetsBreedDisplayName: string | null;
    hasSelectedPetsBreed: boolean;
    showPetsBreedError: boolean;
    showPetHealthPanelError: boolean;
    petVaccinated: string | undefined;
    petNeuteredOrSpayed: string | undefined;
    petDeflead: string | undefined;
    petMicrochipped: string | undefined;
    isDragging: boolean;
    panelAttributesConfig: {
        [panelName: string]: {
            [attributeName: string]: {
                value: string | number | boolean;
                displayName: string;
                error: boolean;
                hasSelected: boolean;
            };
        };
    };
    // show AI generate pop
    hasShowAIgeneratePop: boolean;
    shouldShowFixedSpinner: boolean;
    // homeGarden Attribute

    phoneVerification: {
        advertId: string;
        editorId: string;
        adCategoryId: number;
        triggerPoint: TriggerPoint;
        listingDetails: {} | GA4.GA4ListingDetails;
    } | null;
}

export type SyiRadioCategoryType =
    | 'shared-property'
    | 'entire-property'
    | 'aquarium-only'
    | 'aquarium-fish';

export type SyiImageDef = { fade?: true } & (PostAdImageDef | PlaceholderImageDef);

export interface PlaceholderImageDef {
    /** Provided by response */
    id?: number;
    file: File;
    blobUrl: string;
    waiting: boolean;
    url?: string;
    startTime?: number;
}

export interface Skill {
    skillId: number;
    content: string;
}

const initialState: SyiReducerState = {
    context: {} as any,
    mappedPanels: [],
    remountedAt: -1,

    l1Category: '',
    categorySeoNames: [],
    catList: null,
    expandCategory: false,
    expandCategoryBrowser: false,
    expandLocationBrowser: false,
    expandEditLocation: false,
    enableEditContactDetails: false,
    radioCategoryType: null,
    radioCategoryId: null,
    radioShowAlert: false,
    updateCatsAt: 0,
    openUrgentLightbox: false,
    openFeaturedLightbox: false,
    openSpotlightLightbox: false,
    openWebsiteLinkLightbox: false,
    openSellerTypeLightbox: false,
    openListingCapModal: false,
    hasSavedLocation: false,
    isPostingAd: false,
    panelAttributesConfig: {},
    categorySkills: [],
    isEditedSkills: false,
    accountCategorySkills: [],
    showSellerSkillsError: false,

    formError: {},
    imageDefs: [],
    vrnResponse: null,
    hasFetchVrn: false,
    totalPrice: undefined,
    isDragging: false,
    selectedListingType: null,
    selectedPetsBreed: null,
    selectedPetsBreedDisplayName: null,
    hasSelectedPetsBreed: false,
    showPetsBreedError: false,
    showPetHealthPanelError: false,
    petVaccinated: undefined,
    petNeuteredOrSpayed: undefined,
    petDeflead: undefined,
    petMicrochipped: undefined,
    phoneVerification: null,
    hasShowAIgeneratePop: false,
    shouldShowFixedSpinner: false,
};

export const updateSyi = (payload: Partial<SyiReducerState>) =>
    ({
        type: UPDATE_SYI,
        payload,
    } as const);

export const updateSyiFormError = (payload: {
    elName: string;
    errors?: string[];
    shouldShow?: boolean;
}) =>
    ({
        type: UPDATE_SYI_FORM_ERROR,
        payload,
    } as const);

export const updateSyiPhoneVerification = (payload: {
    advertId: string;
    editorId: string;
    adCategoryId: number;
    listingDetails: {} | GA4.GA4ListingDetails;
    triggerPoint: TriggerPoint;
}) =>
    ({
        type: UPDATE_SYI_PHONE_VERIFY,
        payload,
    } as const);

type ActionUnion =
    | ReturnType<typeof updateSyi>
    | ReturnType<typeof updateSyiFormError>
    | ReturnType<typeof updateSyiPhoneVerification>;

export default function reducer(
    state: SyiReducerState = initialState,
    action: ActionUnion
): SyiReducerState {
    switch (action.type) {
        case 'UPDATE_SYI':
            return {
                ...state,
                ...action.payload,
                panelAttributesConfig: deepMerge(
                    {},
                    state.panelAttributesConfig,
                    action.payload.panelAttributesConfig
                ),
            };
        case 'UPDATE_SYI_FORM_ERROR': {
            const { elName } = action.payload;
            return {
                ...state,
                formError: {
                    ...state.formError,
                    [action.payload.elName]: {
                        elName,
                        errors: action.payload.errors || state.formError[elName]?.errors,
                        shouldShow:
                            action.payload.shouldShow ??
                            state.formError[elName]?.shouldShow ??
                            false,
                    },
                },
            };
        }
        case 'UPDATE_SYI_PHONE_VERIFY':
            return { ...state, phoneVerification: { ...action.payload } };
        default:
            return state;
    }
}

export type MappedPanel = {
    name:
        | string
        | {
              length?: number | undefined;
          };
    data: {
        advertId?: string | undefined;
        categoryCrumb?: SyiModelAttrs['categoryCrumb'];
        locationCrumb?: LocationCrumb;
        postAdImages?: PostAdImageDef[];
        form: {
            title?: string;
            categoryId: number;
            locationId?: number | undefined;
            postcode?: string | undefined;
            attributes: Record<string, any>;
            formErrors: Record<string, any>;
        };
        panels: {
            length?: number | undefined;
        }[];
        pricingMetadata: PricingMetadata;
        redirectTo?: string | undefined;
        status: SyiModelStatus;
        totalPrice?: number | undefined;
    } & {
        panelName:
            | string
            | {
                  length?: number | undefined;
              };
    };
};

export interface CatSuggestionResponse {
    categories: CatSuggestionResponseItem[];
}

export interface CatSuggestionResponseItem {
    id: number;
    index: number;
    displayName: string;
    /** e.g. `For Sale,Baby & Kids Stuff,Toys` */
    tree: string;
    categoryCrumb?: {
        ad_category: string;
        ad_subcategory1: string;
        ad_subcategory2: string;
        ad_subcategory3: string;
        ad_subcategory4: string;
        ad_subcategory5: string;
        ad_category_id: string;
    };
}

export type SyiModelStatus =
    | 'CATEGORY'
    | 'EDIT'
    | 'FORM_FREE'
    | 'FORM_PAID'
    | 'LOCATION'
    | 'LOCATION_UPDATE'
    | 'SELLER'
    | 'REPOST';

export const getPageStateThunk: ActionCreator<
    ThunkAction<SyiState, SyiState, void, Action>
> = () => {
    return (_dispatch, getState) => getState();
};

function getCategorySearchWord() {
    const searchInput = document.getElementById('post-ad_title-suggestion') as HTMLInputElement;
    if (!searchInput) return;
    return searchInput.value;
}

const getFormAttributesSuggestions = async (result, syiAiPostOnExperiment, urls?: string[]) => {
    const form = result.form;
    const categoryName = result.categoryCrumb?.find((x) => x.id === form.categoryId)?.name ?? '';
    let type;
    let input;
    let secInput;
    if (syiAiPostOnExperiment === GROWTHBOOK_VALUE_SYI_TITLE_FIRST) {
        type = 'title';
        input = form.title ?? '';
    } else if (syiAiPostOnExperiment === GROWTHBOOK_VALUE_SYI_PHOTO_FIRST) {
        type = 'img';
        input = result.postAdImages?.map(({ url }) => url).join(',') ?? '';
    } else {
        type = 'all';
        input = form.title ?? '';
        secInput = urls?.join(',') ?? '';
    }
    return await dtlSyi.getFormAttributesSuggestions(input, secInput, type, categoryName);
};

export const clickContinueCategoryThunk: (
    howCategorySelected: 'browse' | 'suggestion',
    listingDetails?: Partial<GA4.GA4ListingDetails>,
    selectedSuggestCategoryOrder?: number
) => ThunkAction<void, SyiState, void, Action> = (
    howCategorySelected,
    listingDetails,
    selectedSuggestCategoryOrder = 0
) => {
    return async (dispatch, getState) => {
        const {
            baseConfig: { bffUrl },
            request: { path },
            syi,
        } = getState();

        const categoryIdHiddenInput = document.querySelector(
            '[name=categoryId]'
        ) as HTMLInputElement;
        const categoryId = Number(categoryIdHiddenInput.value);
        const searchWord = getCategorySearchWord();

        dtlSyi.updateSellerSkills(categoryId);

        const submitPath = ['/postad/create', '/postad/edit', '/postad/repost'];
        const isOnAiCollectPage = path === '/postad/category' && syi.context.queryParam?.categoryId;
        const syiFlowParam = getTrackLogOnCookie();
        if (dtlSyi.form && (submitPath.includes(path) || isOnAiCollectPage)) {
            // /postad/{create,edit,repost}
            dtlSyi.form.updateElements({ termsAgreed: null });
            dtlSyi.form.submitInstant = true;
            await dtlSyi.form.submit(isOnAiCollectPage ? 'saveCategoryOnAttributeStep' : undefined);
            dispatch(
                updateSyi({
                    expandCategory: false,
                    expandCategoryBrowser: false,
                })
            );

            trackGA4Event<GA4.PostAdEvent>({
                event: 'ad_posted_form_progression',
                formName: 'syi-form',
                formStep: 'change-category',
                formValidation: 'success',
                adCategorySelected: categoryId,
                howCategorySelected,
                searchWord,
                selectedSuggestCategoryOrder,
                ...syiFlowParam,
                ...computeListingDetails(),
            });
        } else {
            // /postad/category
            trackGA4PreNav<GA4.PostAdEvent>({
                event: 'ad_posted_form_progression',
                formName: 'syi-form',
                formStep: 'set-category',
                formValidation: 'success',
                searchWord,
                selectedSuggestCategoryOrder,
                listingDetails: listingDetails ?? {},
                adCategorySelected: categoryId,
                howCategorySelected,
                ...syiFlowParam,
            });

            const isOnTitleOrPhoneFirstExperiment =
                dispatch(isSyiAiPostOnExperiment()) && !syi.context.queryParam?.categoryId;

            if (isOnTitleOrPhoneFirstExperiment) {
                const result = await dtlSyi.submitDraft(true);
                if (Number(listingDetails?.subcategory1) === CATEGORY_ID_FOR_SALE) {
                    const hasAttribute = result?.panels?.includes(PanelNameMapping.PRECONFIRM);
                    if (
                        hasAttribute ||
                        syi.context.syiAiPostOnExperiment === GROWTHBOOK_VALUE_SYI_TITLE_FIRST
                    ) {
                        try {
                            await getFormAttributesSuggestions(
                                result,
                                syi.context.syiAiPostOnExperiment
                            );
                        } catch (error) {
                            console.log('getFormAttributesSuggestions failed', error);
                        }

                        window.location.href = `${bffUrl}/postad/category?${new URLSearchParams({
                            categoryId: `${categoryId}`,
                        })}`;
                        return;
                    }
                }
            }

            window.location.href = `${bffUrl}/postad/create?${new URLSearchParams({
                categoryId: `${categoryId}`,
            })}`;
        }
    };
};

export const clickPostcodeGoThunk: ActionCreator<
    ThunkAction<void, SyiState, void, Action>
> = () => {
    return async (dispatch) => {
        if (dtlSyi.form) {
            dtlSyi.form.submitInstant = true;

            const resp = await dtlSyi.submitDraft();

            const hasErrors = resp && resp.form.formErrors.postcode?.length > 0;

            if (hasErrors) {
                dispatch(
                    updateSyiFormError({
                        elName: 'postcode',
                        errors: resp?.form?.formErrors['postcode'],
                        shouldShow: true,
                    })
                );
            } else {
                const resp = await dtlSyi.form.submit();
                const noErrors = resp && Object.keys(resp.form.formErrors).length === 0;
                dispatch(
                    updateSyiFormError({
                        elName: 'postcode',
                        errors: [],
                    })
                );

                dispatch(
                    updateSyi({
                        ...(noErrors && { expandEditLocation: false }),
                    })
                );
            }
        }
    };
};

export const clickContinueLocationOrSellerTypeThunk = (
    type: 'location' | 'seller-type'
): ThunkAction<void, SyiState, void, Action> => {
    return async (dispatch, _getState) => {
        dtlSyi.form!.submitInstant = true;
        await dtlSyi.form!.submit();
        dispatch(
            updateSyi({
                expandEditLocation: false,
                expandLocationBrowser: false,
            })
        );

        const eventName = postAdPageTypeToAdEventName(true);
        eventName &&
            trackGA4PreNav<GA4.PostAdEvent>({
                event: eventName,
                formName: 'syi-form',
                formStep: type === 'location' ? 'set-location' : 'set-seller-type',
                formValidation: 'success',
                ...computeListingDetails(),
            });
    };
};

export const selectListingTypeThunk = (
    type: 'regular' | 'auction'
): ThunkAction<void, SyiState, void, Action> => {
    return async (dispatch, _getState) => {
        const updatedState = dispatch(
            updateSyi({
                selectedListingType: type,
            })
        );

        dtlSyi.loadTemplates();

        return updatedState;
    };
};

export const agreeLegalTermsThunk: ActionCreator<
    ThunkAction<void, SyiState, void, Action>
> = () => {
    return async (dispatch, _getState) => {
        if (dtlSyi.form) {
            // Future SYI
            dtlSyi.form.updateElements({ termsAgreed: 'true' });
            dtlSyi.form.submitInstant = true;
            await dtlSyi.form.submit();
            dispatch(
                updateSyi({
                    expandCategory: false,
                    expandCategoryBrowser: false,
                })
            );
        }
    };
};

export const updateElementBlurChange: (
    elName: string,
    value?: string
) => ThunkAction<void, SyiState, void, Action> = (elName, _value) => {
    return (dispatch, getState) => {
        const {
            syi: { hasShowAIgeneratePop, context },
        } = getState();
        if (context.postAdPageType !== 'create') {
            return;
        }
        if (context.activeCategoryHierarchy?.[0].id !== CATEGORY_ID_FOR_SALE) {
            return;
        }
        if (!dispatch(isSyiAiPostOnExperiment())) {
            return;
        }
        if (hasShowAIgeneratePop) {
            return;
        }
        if (
            ![
                PanelNameMapping.AD_TITLE,
                PanelNameMapping.IMAGES,
                PanelNameMapping.MOBILE_PHONES_ATTRIBUTES_PANEL,
            ].includes(elName as 'ad-title' | 'images' | 'mobile-phones-attributes-panel')
        ) {
            return;
        }
        dispatch(updateSyi({ hasShowAIgeneratePop: true }));
    };
};

let lockGenerateTitleAndDesc = false;
export const regenerateTitleAndDesc = (
    type: 'title' | 'description' | 'all',
    shouldShowFixedSpinner: boolean
): ThunkAction<void, SyiState, void, Action> => {
    return async (dispatch, getState) => {
        const {
            syi: { mappedPanels, imageDefs, context },
        } = getState();

        if (
            context.activeCategoryHierarchy?.[0].id !== CATEGORY_ID_FOR_SALE ||
            context.postAdPageType !== 'create' ||
            !dispatch(isSyiAiPostOnExperiment())
        ) {
            return;
        }

        const formJson = dtlSyi.form?.formJson;
        if (!formJson) {
            return;
        }

        if (lockGenerateTitleAndDesc) {
            return;
        }
        lockGenerateTitleAndDesc = true;

        const categoryCrumb = mappedPanels?.[0]?.data.categoryCrumb || [];
        const categoryName = categoryCrumb[categoryCrumb.length - 1].name ?? '';
        const imageUrls = imageDefs.map(({ url }) => url);
        const params = {
            title: formJson.title,
            categoryName: categoryName,
            attributes: formJson.attributes,
            images: imageUrls.join(','),
            desc: formJson.description,
        };

        shouldShowFixedSpinner && dispatch(updateSyi({ shouldShowFixedSpinner: true }));
        try {
            let result = await dtlSyi.getTitleAndDescSuggestions(params);
            if (result) {
                const target = type === 'all' ? result : { [type]: result[type] };
                dtlSyi.form?.updateElements(target);
            }
        } catch (error) {
            console.log('regenerateTitleAndDesc error', error);
        }
        shouldShowFixedSpinner && dispatch(updateSyi({ shouldShowFixedSpinner: false }));
        lockGenerateTitleAndDesc = false;
    };
};

let lockGenerateAttributes = false;
/**
 * Generate attributes for the current form
 */
export const generateAttributesByImageUrls = (
    urls: string[]
): ThunkAction<void, SyiState, void, Action> => {
    return async (_dispatch, getState) => {
        const {
            syi: { mappedPanels, context },
            request: { path },
        } = getState();

        const isOnAiCollectPage = path === '/postad/category' && context.queryParam?.categoryId;
        if (!isOnAiCollectPage) {
            return;
        }

        const defaultPanelArr = [
            PanelNameMapping.CATEGORY,
            PanelNameMapping.IMAGES,
            PanelNameMapping.PRECONFIRM,
        ];
        const attributePannels = mappedPanels.filter(
            ({ name }) => !defaultPanelArr.includes(name as typeof defaultPanelArr[number])
        );

        if (attributePannels.length === 0) {
            return;
        }

        if (lockGenerateAttributes) {
            return;
        }
        lockGenerateAttributes = true;

        try {
            const attrsSuggestions = await getFormAttributesSuggestions(
                mappedPanels[0].data,
                '',
                urls
            );
            let updatedAttrs = {};
            const formJson = dtlSyi.form?.formJson;
            const attributes = formJson?.attributes;
            const attrs = Object.keys(attrsSuggestions);
            if (attrs.length > 0) {
                attrs.forEach((attr) => {
                    if (!attributes?.[attr]) {
                        updatedAttrs[`attributes[${attr}]`] = attrsSuggestions[attr];
                    }
                });
                dtlSyi.form?.updateElements(updatedAttrs);
                await dtlSyi.form?.submit('saveCategoryOnAttributeStep');
            }
        } catch (error) {
            console.log('error', error);
        }
        lockGenerateAttributes = false;
    };
};

/**
 * - "Sofa Beds & Futons" sofa-beds-futons 679
 * - "Sofa, Armchairs & Suites" sofas 151
 * - "Single Beds" single-beds 148
 * - "Double Beds" double-beds 149
 * - "Mattresses" mattresses 4610
 * - "Wardrobes, Shelving & Storage" wardrobes-shelving-storage 4612
 */
export function isPaidForSaleCategory(categoryId: number): boolean {
    return [
        679, // sofa-beds-futons
        151, // sofas
        148, // single-beds
        149, // double-beds
        4610, // mattresses
        4612, // wardrobes-shelving-storage
    ].includes(categoryId);
}

/**
 * whether it is in the AI post experiment
 */
export const isSyiAiPostOnExperiment = (): ThunkAction<boolean, SyiState, void, Action> => {
    return (_dispatch, getState) => {
        const {
            syi: { context },
        } = getState();
        const { syiAiPostOnExperiment } = context;
        return [GROWTHBOOK_VALUE_SYI_TITLE_FIRST, GROWTHBOOK_VALUE_SYI_PHOTO_FIRST].includes(
            syiAiPostOnExperiment
        );
    };
};

export const generateTrackLogOnCookie = (
    forceRegenerateRequestId?: boolean
): ThunkAction<boolean, SyiState, void, Action> => {
    return (_dispatch, getState) => {
        const {
            syi: { context },
        } = getState();

        const trackLog = getTrackLogOnCookie();
        const requestId = forceRegenerateRequestId ? uuidv4() : trackLog?.requestId || uuidv4();
        const draftId = context.saveFetchURL.split('/').reverse()[0];
        setCookieValue(tracklogKeyOnSyi, {
            requestId,
            draftId: draftId.length === 32 ? draftId : '',
            postType: context.syiAiPostOnExperiment,
        });
        return true;
    };
};

export const trackOnSyiEntry = (): ThunkAction<boolean, SyiState, void, Action> => {
    return (_dispatch, _getState) => {
        trackGA4EventAfterPageView<GA4.SyiEntryPageEvent>({
            event: 'syi_entry_page',
            ...getTrackLogOnCookie(),
        });
        return true;
    };
};

export const getTrackLogOnCookie = () => {
    const trackLog = getCookieValue(tracklogKeyOnSyi);
    return {
        requestId: trackLog?.requestId ?? '',
        draftId: trackLog?.draftId ?? '',
    };
};
